import { useMemo } from 'react';
import type { TypologyServiceTakeoffInput } from '@obraplan/core/application/contracts/schedule-from-takeoff';
import type { Session } from '@obraplan/core/application/session';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import type { Container } from '@/app/container';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';
import { quantitativoDaObra, type LeiturasDoQuantitativo } from './takeoff-lines';

/**
 * O quantitativo da obra aberta, pronto para o cronograma (Fase 31).
 *
 * - `DESLIGADO`: ninguém pediu — a tela não lê o quantitativo à toa, que é a leitura mais
 *   cara da obra (o desenho inteiro e a conta de cada tipologia);
 * - `FALHOU`: alguma leitura não veio, e a tela **diz**: mandar um quantitativo pela metade
 *   geraria um cronograma pela metade, e o vazio o servidor recusaria sem dizer por quê;
 * - `VAZIO`: leu tudo, e nenhuma tipologia tem número.
 */
export type QuantitativoMontado<L> =
  | { readonly estado: 'DESLIGADO' }
  | { readonly estado: 'CARREGANDO' }
  | { readonly estado: 'FALHOU'; readonly motivo: string }
  | { readonly estado: 'VAZIO' }
  | { readonly estado: 'PRONTO'; readonly linhas: readonly L[] };

/** O quantitativo por tipologia × serviço, o do cronograma por mão de obra (Fase 31). */
export type QuantitativoDoCronograma = QuantitativoMontado<TypologyServiceTakeoffInput>;

const DESLIGADO = { estado: 'DESLIGADO' } as const;
const CARREGANDO = { estado: 'CARREGANDO' } as const;
const VAZIO = { estado: 'VAZIO' } as const;

type Leitura =
  | { readonly ok: true; readonly leituras: LeiturasDoQuantitativo }
  | { readonly ok: false; readonly motivo: string };

/** As cinco leituras da tela do quantitativo, numa vez só e da mesma obra. */
async function lerQuantitativo(
  container: Container,
  projectId: ProjectId,
  session: Session,
): Promise<Leitura> {
  try {
    const [estrutura, quantitativo, geometria, servicos, itens] = await Promise.all([
      container.getProjectStructure.execute(projectId, session),
      container.getProjectTakeoff.execute(projectId, session),
      container.getProjectGeometry.execute(projectId, session),
      container.getProjectServices.execute(projectId, session),
      container.getProjectServiceItems.execute(projectId, session),
    ]);
    const leituras: readonly [string, { ok: boolean; error?: { message: string } }][] = [
      ['a estrutura não foi lida', estrutura],
      ['as tipologias não foram lidas', quantitativo],
      ['o desenho não foi lido', geometria],
      ['o cadastro de serviços não foi lido', servicos],
      ['os itens dos serviços não foram lidos', itens],
    ];
    const falha = leituras.find(([, resultado]) => !resultado.ok);
    if (falha !== undefined) {
      const [frase, resultado] = falha;
      const detalhe = resultado.error?.message;
      return { ok: false, motivo: detalhe === undefined ? `${frase}.` : `${frase} (${detalhe})` };
    }
    // Já conferido acima; repetido para o TypeScript estreitar cada `Result`.
    if (!estrutura.ok || !quantitativo.ok || !geometria.ok || !servicos.ok || !itens.ok) {
      return { ok: false, motivo: 'o quantitativo não foi lido.' };
    }
    return {
      ok: true,
      leituras: {
        estrutura: estrutura.value,
        quantitativo: quantitativo.value,
        geometria: geometria.value,
        servicos: servicos.value,
        itens: itens.value,
      },
    };
  } catch {
    return { ok: false, motivo: 'o quantitativo não foi lido — verifique a conexão.' };
  }
}

/**
 * Lê o quantitativo da obra aberta e o monta por tipologia × serviço (`takeoff-lines.ts`).
 *
 * Só lê com `ativo`: a faixa de revisão o liga quando a obra tem item do quantitativo, e o
 * "Gerar do quantitativo" quando abre. As duas compartilham a mesma leitura — quem chama é a
 * página, uma vez.
 */
export function useTakeoffLines(ativo: boolean): QuantitativoDoCronograma {
  return useQuantitativoMontado(ativo, quantitativoDaObra);
}

/**
 * As mesmas cinco leituras, montadas por `montar` — a do cronograma por mão de obra
 * (`quantitativoDaObra`) ou a do cronograma por item (`materiaisDaObra`, Fase 35). `montar`
 * precisa ser estável (uma função de módulo): ela entra na memória do resultado.
 */
export function useQuantitativoMontado<L>(
  ativo: boolean,
  montar: (leituras: LeiturasDoQuantitativo) => readonly L[],
): QuantitativoMontado<L> {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const ligado = ativo && session !== undefined && activeProjectId !== undefined;

  const leitura = useLive(
    () =>
      !ligado
        ? Promise.resolve(undefined)
        : lerQuantitativo(container, activeProjectId, session).then((lida) => ({
            de: activeProjectId,
            lida,
          })),
    [ligado, session?.userId, activeProjectId],
  );

  return useMemo<QuantitativoMontado<L>>(() => {
    if (!ligado) return DESLIGADO;
    // A leitura que ainda é da obra anterior não vale para esta.
    if (leitura === undefined || leitura.de !== activeProjectId) return CARREGANDO;
    if (!leitura.lida.ok) return { estado: 'FALHOU', motivo: leitura.lida.motivo };
    try {
      const linhas = montar(leitura.lida.leituras);
      return linhas.length === 0 ? VAZIO : { estado: 'PRONTO', linhas };
    } catch (causa) {
      console.error('[obraplan] o quantitativo não fechou a conta', causa);
      return { estado: 'FALHOU', motivo: 'a conta do quantitativo não fechou.' };
    }
  }, [ligado, leitura, activeProjectId, montar]);
}

/** A frase do estado, para quem precisa dizer por que não há quantitativo. */
export function motivoSemQuantitativo(
  quantitativo: QuantitativoMontado<unknown>,
): string | undefined {
  if (quantitativo.estado === 'FALHOU') {
    return `Não foi possível ler o quantitativo da obra: ${quantitativo.motivo}`;
  }
  if (quantitativo.estado === 'VAZIO') {
    return 'O quantitativo da obra ainda não tem número: nenhuma tipologia tem quantidade ou horas lançadas.';
  }
  return undefined;
}
